/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-11
 */

import { createContext, useContext, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { useThemeMode, ThemeMode } from '../hooks/useThemeMode';
import { createAppTheme } from '../theme/theme';

interface ThemeContextType {
  mode: ThemeMode;
  toggleMode: () => void;
  setThemeMode: (mode: ThemeMode) => void;
  isLight: boolean;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Theme provider component that wraps the app with theme context and Material-UI theme provider.
 * @param props - Component props
 * @param props.children - Child components to wrap
 * @returns Theme provider component
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const themeMode = useThemeMode();
  const theme = createAppTheme(themeMode.mode);

  return (
    <ThemeContext.Provider value={themeMode}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

/**
 * Hook to access theme context.
 * @returns Theme context value
 * @throws Error if used outside of ThemeProvider
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeProvider;
